import React, { useContext } from 'react';
import PetProfileForm from '@chewy/pet-profile-form';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import { Clinics } from '../../../components/pet-form/VetClinic/vetClinicResponse';
import AnalyticsContext from '../../../context/AnalyticsContext';
import useLocationQuery from '../../../hooks/useLocationQuery';
import AvatarSelectionEdit from '../../pet-form/AvatarSelection/editPage/AvatarSelectionEdit';
import { PetFormValues, usePetForm } from '../../pet-form/PetFormContext/PetFormContext';
import EditImage from '../EditImage/EditImage';
import {
  ApiPetProfile,
  ApiPetProfileAvatar,
  ApiPetProfileAvatarImageSourceEnum,
} from '../../../api/generated-client/PetProfile-Service-A.v1/models';
import { getPetEditPath, getPetManagePath, getPetProfilePath } from '../../../util/config';
import '@chewy/pet-profile-form/dist/esm/main.css';
import './EditFormConfiguredV2.scss';
import { ListVetClinic } from '../../pet-form/VetClinic/ListVetClinic';
import { getActivePets, getPet } from '../../../api/petApi';
import { conditionalHistoryPush } from '../../../util/helpers';

export const REMOVE_PROFILE_BUTTON_LABEL = 'Memorialize or remove profile';

export const getMemorialLink = (redirectUrl: string | null, petProfileId?: number) =>
  redirectUrl
    ? getPetManagePath(petProfileId) +
      `?redirectUrl=${encodeURIComponent(redirectUrl)}` +
      `&innerRedirectUrl=${encodeURIComponent(getPetEditPath(petProfileId))}`
    : getPetManagePath(petProfileId);

const getAvatarValue = (formValues: PetFormValues): ApiPetProfileAvatar => {
  const avatar: ApiPetProfileAvatar = {
    imageUri: formValues.avatarUrl,
    imageSource: formValues.avatarSource,
  };
  if (avatar.imageSource === ApiPetProfileAvatarImageSourceEnum.Content) {
    avatar.id = Number(formValues.avatarId);
  }
  return avatar;
};

export const getCoverPhotoValue = (
  formValues: PetFormValues,
):
  | string // new upload
  | null // removal
  // no-change
  | undefined => {
  const bannerUrl = formValues.bannerUrl;
  const image = formValues.image;
  if (!!bannerUrl) {
    return bannerUrl;
  } else if (image === '') {
    return null;
  }
  return;
};

const getSPAFormMap = (formValues: PetFormValues) => {
  const coverPhotoEntry = getCoverPhotoValue(formValues);
  const extMap = new Map<string, any>([
    [
      'avatar',
      {
        errors: [],
        value: getAvatarValue(formValues),
      },
    ],
  ]);
  if (coverPhotoEntry !== undefined) {
    extMap.set('bannerImageUri', {
      errors: [],
      value: coverPhotoEntry,
    });
  }
  return extMap;
};

export const EditFormHeaderComponent = ({
  loadingAvatarOptionsError,
  petName,
}: {
  loadingAvatarOptionsError?: boolean;
  petName: string;
}) => (
  <>
    {!loadingAvatarOptionsError ? (
      <AvatarSelectionEdit />
    ) : (
      <h1 className="edit-form__title">{petName}&apos;s Profile</h1>
    )}
    <EditImage />
  </>
);

export interface EditFormProps {
  handleNavigation?: () => void;
  clinics: Clinics;
}

const EditFormConfiguredV2 = ({ handleNavigation, clinics }: EditFormProps) => {
  const { formValues, petProfileId, loadingAvatarOptionsError } = usePetForm();

  const query = useLocationQuery();
  const analytics = useContext(AnalyticsContext);
  const redirectUrl = query.get('redirectUrl');
  const queryClient = useQueryClient();
  const history = useHistory();

  const dismissCallback = () => {
    if (!redirectUrl) {
      conditionalHistoryPush(getPetProfilePath(petProfileId), history);
    } else {
      window.location.href = decodeURIComponent(redirectUrl);
    }
  };

  const invalidateQueries = (id?: number) => {
    const invalidQueries = [
      queryClient.removeQueries(getActivePets.key),
      queryClient.removeQueries([getPet.key, `${id}`]),
    ];
    return Promise.all(invalidQueries);
  };

  const memorialLink = getMemorialLink(redirectUrl, petProfileId);

  const onPetUpdate = (pet: ApiPetProfile) => {
    invalidateQueries(pet.id).finally(() => {
      if (!!handleNavigation) {
        handleNavigation();
      }
    });
  };

  return (
    <div
      className="edit-form container js-test-activation-trigger edit-form-api-driven"
      data-testid="api-driven-v2-pet-form"
    >
      {/* Pet Form Component */}
      <PetProfileForm
        externalPetFormMap={getSPAFormMap(formValues)}
        petProfileId={petProfileId}
        headerComponent={
          <EditFormHeaderComponent
            petName={formValues.name}
            loadingAvatarOptionsError={loadingAvatarOptionsError}
          />
        }
        analytics={{
          segmentTrack: analytics.trackEvent,
        }}
        petUpdateSuccessCallback={onPetUpdate}
        dismissCallback={dismissCallback}
        footerComponent={<ListVetClinic clinics={clinics} />}
      />
      <div className="edit-form__manage-button-container">
        <button
          className="edit-form__manage-button"
          data-testid="edit-form-manage-profile-btn"
          onClick={() => {
            window.location.href = memorialLink;
            analytics.trackRainbow({
              name: 'Edit Pet Profile - Clicked',
              category: 'edit-pet-profile',
              action: 'click',
              label: 'edit_profile_remove_memorialize',
            });
          }}
        >
          {REMOVE_PROFILE_BUTTON_LABEL}
        </button>
      </div>
    </div>
  );
};

export default EditFormConfiguredV2;
